export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export type Database = {
  // Allows to automatically instantiate createClient with right options
  // instead of createClient<Database, { PostgrestVersion: 'XX' }>(URL, KEY)
  __InternalSupabase: {
    PostgrestVersion: "12.2.3 (519615d)"
  }
  internal: {
    Tables: {
      kvs: {
        Row: {
          key: string
          value: string
        }
        Insert: {
          key: string
          value: string
        }
        Update: {
          key?: string
          value?: string
        }
        Relationships: []
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  public: {
    Tables: {
      action_items_data: {
        Row: {
          created_at: string
          data: Json | null
          skipped_files: Database["public"]["Enums"]["file_type"][]
          type: Database["public"]["Enums"]["conversation_type"]
          updated_at: string
          user_id: string
        }
        Insert: {
          created_at?: string
          data?: Json | null
          skipped_files?: Database["public"]["Enums"]["file_type"][]
          type: Database["public"]["Enums"]["conversation_type"]
          updated_at?: string
          user_id: string
        }
        Update: {
          created_at?: string
          data?: Json | null
          skipped_files?: Database["public"]["Enums"]["file_type"][]
          type?: Database["public"]["Enums"]["conversation_type"]
          updated_at?: string
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "action_items_data_users_id_fk"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      assets: {
        Row: {
          created_at: string
          deleted_at: string | null
          id: string
          name: string
          object_id: string
          owner_id: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          deleted_at?: string | null
          id?: string
          name: string
          object_id: string
          owner_id: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          deleted_at?: string | null
          id?: string
          name?: string
          object_id?: string
          owner_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "assets_users_id_fk"
            columns: ["owner_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      company_info: {
        Row: {
          created_at: string
          logo_asset: string | null
          name: string
          updated_at: string
          user_id: string
        }
        Insert: {
          created_at?: string
          logo_asset?: string | null
          name: string
          updated_at?: string
          user_id: string
        }
        Update: {
          created_at?: string
          logo_asset?: string | null
          name?: string
          updated_at?: string
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "company_info_assets_id_fk"
            columns: ["logo_asset"]
            isOneToOne: false
            referencedRelation: "assets"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "company_info_users_id_fk"
            columns: ["user_id"]
            isOneToOne: true
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      conversation_message_processing: {
        Row: {
          created_at: string
          exp_router_errors: Json | null
          exp_router_raw_responses: Json | null
          exp_router_reasoning: string | null
          exp_router_verdict: Json | null
          message_id: string
          processing_success: boolean | null
          router_errors: Json | null
          router_raw_responses: Json | null
          router_reasoning: string | null
          router_verdict: Json | null
        }
        Insert: {
          created_at?: string
          exp_router_errors?: Json | null
          exp_router_raw_responses?: Json | null
          exp_router_reasoning?: string | null
          exp_router_verdict?: Json | null
          message_id: string
          processing_success?: boolean | null
          router_errors?: Json | null
          router_raw_responses?: Json | null
          router_reasoning?: string | null
          router_verdict?: Json | null
        }
        Update: {
          created_at?: string
          exp_router_errors?: Json | null
          exp_router_raw_responses?: Json | null
          exp_router_reasoning?: string | null
          exp_router_verdict?: Json | null
          message_id?: string
          processing_success?: boolean | null
          router_errors?: Json | null
          router_raw_responses?: Json | null
          router_reasoning?: string | null
          router_verdict?: Json | null
        }
        Relationships: [
          {
            foreignKeyName: "conversation_message_processing_message_id_fkey"
            columns: ["message_id"]
            isOneToOne: true
            referencedRelation: "conversation_messages"
            referencedColumns: ["id"]
          },
        ]
      }
      conversation_messages: {
        Row: {
          author: Database["public"]["Enums"]["message_author"]
          conversation_id: string
          created_at: string
          id: string
          is_preview_ready: boolean | null
          is_synthetic: boolean
          message: string
          metadata: Json | null
          response_id: string | null
          status: string | null
          tag: string | null
        }
        Insert: {
          author: Database["public"]["Enums"]["message_author"]
          conversation_id: string
          created_at?: string
          id?: string
          is_preview_ready?: boolean | null
          is_synthetic?: boolean
          message: string
          metadata?: Json | null
          response_id?: string | null
          status?: string | null
          tag?: string | null
        }
        Update: {
          author?: Database["public"]["Enums"]["message_author"]
          conversation_id?: string
          created_at?: string
          id?: string
          is_preview_ready?: boolean | null
          is_synthetic?: boolean
          message?: string
          metadata?: Json | null
          response_id?: string | null
          status?: string | null
          tag?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "fk_conversation"
            columns: ["conversation_id"]
            isOneToOne: false
            referencedRelation: "conversations"
            referencedColumns: ["id"]
          },
        ]
      }
      conversation_summary: {
        Row: {
          abuse_detect: boolean
          abuse_prob: number
          business_pain: string
          conversation_id: string
          created_at: string
          frustration: number
          id: string
          last_message_id: string
          platform_pain: string
          quality: number
          self_reflect: string
          state: string
          status: string
          summary: string
        }
        Insert: {
          abuse_detect: boolean
          abuse_prob: number
          business_pain: string
          conversation_id: string
          created_at?: string
          frustration: number
          id?: string
          last_message_id: string
          platform_pain: string
          quality: number
          self_reflect: string
          state: string
          status: string
          summary: string
        }
        Update: {
          abuse_detect?: boolean
          abuse_prob?: number
          business_pain?: string
          conversation_id?: string
          created_at?: string
          frustration?: number
          id?: string
          last_message_id?: string
          platform_pain?: string
          quality?: number
          self_reflect?: string
          state?: string
          status?: string
          summary?: string
        }
        Relationships: [
          {
            foreignKeyName: "fk_conversation"
            columns: ["conversation_id"]
            isOneToOne: false
            referencedRelation: "conversations"
            referencedColumns: ["id"]
          },
        ]
      }
      conversations: {
        Row: {
          created_at: string
          id: string
          last_response_id: string | null
          metadata: Json | null
          processing: string | null
          ready: boolean
          stage: Database["public"]["Enums"]["conversation_stage"]
          state: string
          type: Database["public"]["Enums"]["conversation_type"]
          user_id: string
        }
        Insert: {
          created_at?: string
          id?: string
          last_response_id?: string | null
          metadata?: Json | null
          processing?: string | null
          ready?: boolean
          stage?: Database["public"]["Enums"]["conversation_stage"]
          state?: string
          type: Database["public"]["Enums"]["conversation_type"]
          user_id: string
        }
        Update: {
          created_at?: string
          id?: string
          last_response_id?: string | null
          metadata?: Json | null
          processing?: string | null
          ready?: boolean
          stage?: Database["public"]["Enums"]["conversation_stage"]
          state?: string
          type?: Database["public"]["Enums"]["conversation_type"]
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "fk_user"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      dataroom_access: {
        Row: {
          approved: boolean | null
          created_at: string
          expires_at: string | null
          owner_id: string
          updated_at: string
          visitor_id: string
        }
        Insert: {
          approved?: boolean | null
          created_at?: string
          expires_at?: string | null
          owner_id: string
          updated_at?: string
          visitor_id: string
        }
        Update: {
          approved?: boolean | null
          created_at?: string
          expires_at?: string | null
          owner_id?: string
          updated_at?: string
          visitor_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "dataroom_access_owner_id_users_id_fk"
            columns: ["owner_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "dataroom_access_visitor_id_users_fk"
            columns: ["visitor_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      dataroom_invite: {
        Row: {
          created_at: string
          deleted_at: string | null
          email: string
          expires_at: string | null
          id: string
          owner_id: string
        }
        Insert: {
          created_at?: string
          deleted_at?: string | null
          email: string
          expires_at?: string | null
          id?: string
          owner_id: string
        }
        Update: {
          created_at?: string
          deleted_at?: string | null
          email?: string
          expires_at?: string | null
          id?: string
          owner_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "dataroom_invite_owner_id_users_id_fk"
            columns: ["owner_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      dataroom_visit: {
        Row: {
          action_item_type:
            | Database["public"]["Enums"]["conversation_type"]
            | null
          created_at: string
          id: string
          owner_id: string
          parent_id: string | null
          time_spent: number
          visitor_id: string
        }
        Insert: {
          action_item_type?:
            | Database["public"]["Enums"]["conversation_type"]
            | null
          created_at?: string
          id?: string
          owner_id: string
          parent_id?: string | null
          time_spent?: number
          visitor_id: string
        }
        Update: {
          action_item_type?:
            | Database["public"]["Enums"]["conversation_type"]
            | null
          created_at?: string
          id?: string
          owner_id?: string
          parent_id?: string | null
          time_spent?: number
          visitor_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "dataroom_access_owner_id_users_id_fk"
            columns: ["owner_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "dataroom_access_visitor_id_users_fk"
            columns: ["visitor_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "dataroom_visit_parent_id_fk"
            columns: ["parent_id"]
            isOneToOne: false
            referencedRelation: "dataroom_visit"
            referencedColumns: ["id"]
          },
        ]
      }
      document_generations: {
        Row: {
          contents: string
          created_at: string
          error: boolean
          generated_object_id: string | null
          generated_object_path: string | null
          id: string
          type: Database["public"]["Enums"]["generated_document_type"]
          user_id: string | null
        }
        Insert: {
          contents: string
          created_at?: string
          error?: boolean
          generated_object_id?: string | null
          generated_object_path?: string | null
          id?: string
          type: Database["public"]["Enums"]["generated_document_type"]
          user_id?: string | null
        }
        Update: {
          contents?: string
          created_at?: string
          error?: boolean
          generated_object_id?: string | null
          generated_object_path?: string | null
          id?: string
          type?: Database["public"]["Enums"]["generated_document_type"]
          user_id?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "document_generations_users_id_fk"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      file_extraction_intake: {
        Row: {
          complete: boolean
          created_at: string
          error: boolean
          extracted: Json | null
          file_id: string
          type: Database["public"]["Enums"]["conversation_type"]
          updated_at: string
        }
        Insert: {
          complete?: boolean
          created_at?: string
          error?: boolean
          extracted?: Json | null
          file_id: string
          type: Database["public"]["Enums"]["conversation_type"]
          updated_at?: string
        }
        Update: {
          complete?: boolean
          created_at?: string
          error?: boolean
          extracted?: Json | null
          file_id?: string
          type?: Database["public"]["Enums"]["conversation_type"]
          updated_at?: string
        }
        Relationships: []
      }
      file_extractions: {
        Row: {
          created_at: string
          error: Json | null
          extracted: Json | null
          file_id: string
          file_prompt_id: string
          file_prompt_version: number
        }
        Insert: {
          created_at?: string
          error?: Json | null
          extracted?: Json | null
          file_id: string
          file_prompt_id: string
          file_prompt_version: number
        }
        Update: {
          created_at?: string
          error?: Json | null
          extracted?: Json | null
          file_id?: string
          file_prompt_id?: string
          file_prompt_version?: number
        }
        Relationships: [
          {
            foreignKeyName: "file_extractions_file_prompts_id_version_fk"
            columns: ["file_prompt_id", "file_prompt_version"]
            isOneToOne: false
            referencedRelation: "file_prompts"
            referencedColumns: ["id", "version"]
          },
          {
            foreignKeyName: "file_extractions_file_prompts_id_version_fk"
            columns: ["file_prompt_id", "file_prompt_version"]
            isOneToOne: false
            referencedRelation: "file_prompts_newest"
            referencedColumns: ["id", "version"]
          },
          {
            foreignKeyName: "file_extractions_files_id_fk"
            columns: ["file_id"]
            isOneToOne: false
            referencedRelation: "files"
            referencedColumns: ["id"]
          },
        ]
      }
      file_prompts: {
        Row: {
          active: boolean
          created_at: string
          deleted_at: string | null
          exclusive: boolean
          id: string
          name: string | null
          prompt: string | null
          prompt_adv: Json | null
          purpose: Database["public"]["Enums"]["file_purpose"] | null
          schema: Json | null
          schema_new: Json | null
          target_action_item:
            | Database["public"]["Enums"]["conversation_type"]
            | null
          tokens: number | null
          type: Database["public"]["Enums"]["file_type"] | null
          version: number
        }
        Insert: {
          active?: boolean
          created_at?: string
          deleted_at?: string | null
          exclusive?: boolean
          id?: string
          name?: string | null
          prompt?: string | null
          prompt_adv?: Json | null
          purpose?: Database["public"]["Enums"]["file_purpose"] | null
          schema?: Json | null
          schema_new?: Json | null
          target_action_item?:
            | Database["public"]["Enums"]["conversation_type"]
            | null
          tokens?: number | null
          type?: Database["public"]["Enums"]["file_type"] | null
          version?: number
        }
        Update: {
          active?: boolean
          created_at?: string
          deleted_at?: string | null
          exclusive?: boolean
          id?: string
          name?: string | null
          prompt?: string | null
          prompt_adv?: Json | null
          purpose?: Database["public"]["Enums"]["file_purpose"] | null
          schema?: Json | null
          schema_new?: Json | null
          target_action_item?:
            | Database["public"]["Enums"]["conversation_type"]
            | null
          tokens?: number | null
          type?: Database["public"]["Enums"]["file_type"] | null
          version?: number
        }
        Relationships: []
      }
      files: {
        Row: {
          conversation_message_id: string | null
          converted_object_id: string | null
          created_at: string
          deleted_at: string | null
          flavor: Database["public"]["Enums"]["file_flavor"]
          id: string
          is_processed_by_chat: boolean
          markdown: string | null
          markdown_error: string | null
          name: string
          object_id: string
          openai_file_expiry: string | null
          openai_file_id: string | null
          owner_id: string
          processing_started_at: string | null
          provider: Database["public"]["Enums"]["file_provider"]
          purpose: Database["public"]["Enums"]["file_purpose"] | null
          status: Database["public"]["Enums"]["file_status"]
          type: Database["public"]["Enums"]["file_type"] | null
          updated_at: string
        }
        Insert: {
          conversation_message_id?: string | null
          converted_object_id?: string | null
          created_at?: string
          deleted_at?: string | null
          flavor?: Database["public"]["Enums"]["file_flavor"]
          id?: string
          is_processed_by_chat?: boolean
          markdown?: string | null
          markdown_error?: string | null
          name: string
          object_id: string
          openai_file_expiry?: string | null
          openai_file_id?: string | null
          owner_id: string
          processing_started_at?: string | null
          provider?: Database["public"]["Enums"]["file_provider"]
          purpose?: Database["public"]["Enums"]["file_purpose"] | null
          status: Database["public"]["Enums"]["file_status"]
          type?: Database["public"]["Enums"]["file_type"] | null
          updated_at?: string
        }
        Update: {
          conversation_message_id?: string | null
          converted_object_id?: string | null
          created_at?: string
          deleted_at?: string | null
          flavor?: Database["public"]["Enums"]["file_flavor"]
          id?: string
          is_processed_by_chat?: boolean
          markdown?: string | null
          markdown_error?: string | null
          name?: string
          object_id?: string
          openai_file_expiry?: string | null
          openai_file_id?: string | null
          owner_id?: string
          processing_started_at?: string | null
          provider?: Database["public"]["Enums"]["file_provider"]
          purpose?: Database["public"]["Enums"]["file_purpose"] | null
          status?: Database["public"]["Enums"]["file_status"]
          type?: Database["public"]["Enums"]["file_type"] | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "files_conversation_message_id_fkey"
            columns: ["conversation_message_id"]
            isOneToOne: false
            referencedRelation: "conversation_messages"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "files_users_id_fk"
            columns: ["owner_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      integration_auth: {
        Row: {
          access_token: string | null
          access_token_expiry: string | null
          created_at: string
          integration: Database["public"]["Enums"]["integration_type"]
          refresh_token: string | null
          user_id: string
        }
        Insert: {
          access_token?: string | null
          access_token_expiry?: string | null
          created_at?: string
          integration: Database["public"]["Enums"]["integration_type"]
          refresh_token?: string | null
          user_id: string
        }
        Update: {
          access_token?: string | null
          access_token_expiry?: string | null
          created_at?: string
          integration?: Database["public"]["Enums"]["integration_type"]
          refresh_token?: string | null
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "contacts_sync_user_id_users_id_fk"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      integration_contacts_sync: {
        Row: {
          contact_id: string
          created_at: string
          deleted_at: string | null
          id: string
          id_for_integration: string | null
          integration: Database["public"]["Enums"]["integration_type"]
          last_sync_attempt: string | null
          metadata_for_integration: Json | null
          sync_error: string | null
          synced_email: string | null
          synced_name: string | null
          user_id: string
        }
        Insert: {
          contact_id: string
          created_at?: string
          deleted_at?: string | null
          id?: string
          id_for_integration?: string | null
          integration: Database["public"]["Enums"]["integration_type"]
          last_sync_attempt?: string | null
          metadata_for_integration?: Json | null
          sync_error?: string | null
          synced_email?: string | null
          synced_name?: string | null
          user_id: string
        }
        Update: {
          contact_id?: string
          created_at?: string
          deleted_at?: string | null
          id?: string
          id_for_integration?: string | null
          integration?: Database["public"]["Enums"]["integration_type"]
          last_sync_attempt?: string | null
          metadata_for_integration?: Json | null
          sync_error?: string | null
          synced_email?: string | null
          synced_name?: string | null
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "contacts_sync_contact_id_users_id_fk"
            columns: ["contact_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "contacts_sync_user_id_users_id_fk"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      market_content_results: {
        Row: {
          blog_viewed: boolean
          created_at: string
          data: Json | null
          email_viewed: boolean
          id: string
          linkedin_viewed: boolean
          market_intelligence_viewed: boolean
          twitter_viewed: boolean
          user_id: string
        }
        Insert: {
          blog_viewed?: boolean
          created_at?: string
          data?: Json | null
          email_viewed?: boolean
          id?: string
          linkedin_viewed?: boolean
          market_intelligence_viewed?: boolean
          twitter_viewed?: boolean
          user_id: string
        }
        Update: {
          blog_viewed?: boolean
          created_at?: string
          data?: Json | null
          email_viewed?: boolean
          id?: string
          linkedin_viewed?: boolean
          market_intelligence_viewed?: boolean
          twitter_viewed?: boolean
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "fk_user"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      mikro_orm_migrations: {
        Row: {
          executed_at: string | null
          id: number
          name: string | null
        }
        Insert: {
          executed_at?: string | null
          id?: number
          name?: string | null
        }
        Update: {
          executed_at?: string | null
          id?: number
          name?: string | null
        }
        Relationships: []
      }
      notifications: {
        Row: {
          bumped_at: string
          created_at: string
          custom_payload: Json | null
          id: string
          related_visit_id: string | null
          type: Database["public"]["Enums"]["notification_type"]
          updated_at: string
          user_id: string
          viewed_at: string | null
        }
        Insert: {
          bumped_at?: string
          created_at?: string
          custom_payload?: Json | null
          id?: string
          related_visit_id?: string | null
          type: Database["public"]["Enums"]["notification_type"]
          updated_at?: string
          user_id: string
          viewed_at?: string | null
        }
        Update: {
          bumped_at?: string
          created_at?: string
          custom_payload?: Json | null
          id?: string
          related_visit_id?: string | null
          type?: Database["public"]["Enums"]["notification_type"]
          updated_at?: string
          user_id?: string
          viewed_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "notifications_related_dataroom_visit_fk"
            columns: ["related_visit_id"]
            isOneToOne: false
            referencedRelation: "dataroom_visit"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "notifications_user_id_users_id_fk"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      onboarding: {
        Row: {
          active_step: number
          created_at: string
          data: Json | null
          logo_asset: string | null
          updated_at: string
          user_id: string
        }
        Insert: {
          active_step?: number
          created_at?: string
          data?: Json | null
          logo_asset?: string | null
          updated_at?: string
          user_id: string
        }
        Update: {
          active_step?: number
          created_at?: string
          data?: Json | null
          logo_asset?: string | null
          updated_at?: string
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "onboarding_assets_id_fk"
            columns: ["logo_asset"]
            isOneToOne: false
            referencedRelation: "assets"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "onboarding_users_id_fk"
            columns: ["user_id"]
            isOneToOne: true
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      pdf_theme: {
        Row: {
          metadata: Json | null
          name: string
          theme: string
        }
        Insert: {
          metadata?: Json | null
          name: string
          theme: string
        }
        Update: {
          metadata?: Json | null
          name?: string
          theme?: string
        }
        Relationships: []
      }
      previews: {
        Row: {
          created_at: string
          data: Json | null
          document_generation_hash: string | null
          document_generation_id: string | null
          id: string
          in_dataroom: boolean
          type: Database["public"]["Enums"]["conversation_type"]
          user_id: string
        }
        Insert: {
          created_at?: string
          data?: Json | null
          document_generation_hash?: string | null
          document_generation_id?: string | null
          id?: string
          in_dataroom?: boolean
          type: Database["public"]["Enums"]["conversation_type"]
          user_id: string
        }
        Update: {
          created_at?: string
          data?: Json | null
          document_generation_hash?: string | null
          document_generation_id?: string | null
          id?: string
          in_dataroom?: boolean
          type?: Database["public"]["Enums"]["conversation_type"]
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "fk_user"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "previews_document_generations_id_fk"
            columns: ["document_generation_id"]
            isOneToOne: false
            referencedRelation: "document_generations"
            referencedColumns: ["id"]
          },
        ]
      }
      qa_rag: {
        Row: {
          answer: string
          created_at: string
          embedding: string | null
          for_intake: boolean
          id: string
          metadata: Json | null
          question: string
          tags: string[]
          updated_at: string
        }
        Insert: {
          answer: string
          created_at?: string
          embedding?: string | null
          for_intake?: boolean
          id?: string
          metadata?: Json | null
          question: string
          tags?: string[]
          updated_at?: string
        }
        Update: {
          answer?: string
          created_at?: string
          embedding?: string | null
          for_intake?: boolean
          id?: string
          metadata?: Json | null
          question?: string
          tags?: string[]
          updated_at?: string
        }
        Relationships: []
      }
      storage_object_owners: {
        Row: {
          object_id: string
          user_id: string
        }
        Insert: {
          object_id: string
          user_id: string
        }
        Update: {
          object_id?: string
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "storage_object_owners_users_id_fk"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      subscription_caps: {
        Row: {
          adv_analytics: boolean
          automation_workflows: boolean
          basic_analytics: boolean
          basic_analytics_unlocked: boolean
          content_freq:
            | Database["public"]["Enums"]["generation_frequency_enum"]
            | null
          export: boolean
          free: boolean
          id: string
          integrations: boolean
          investors_unmasked: boolean
          market_freq:
            | Database["public"]["Enums"]["generation_frequency_enum"]
            | null
          name: string | null
          support_priority: boolean
        }
        Insert: {
          adv_analytics: boolean
          automation_workflows: boolean
          basic_analytics: boolean
          basic_analytics_unlocked: boolean
          content_freq?:
            | Database["public"]["Enums"]["generation_frequency_enum"]
            | null
          export: boolean
          free?: boolean
          id?: string
          integrations: boolean
          investors_unmasked: boolean
          market_freq?:
            | Database["public"]["Enums"]["generation_frequency_enum"]
            | null
          name?: string | null
          support_priority: boolean
        }
        Update: {
          adv_analytics?: boolean
          automation_workflows?: boolean
          basic_analytics?: boolean
          basic_analytics_unlocked?: boolean
          content_freq?:
            | Database["public"]["Enums"]["generation_frequency_enum"]
            | null
          export?: boolean
          free?: boolean
          id?: string
          integrations?: boolean
          investors_unmasked?: boolean
          market_freq?:
            | Database["public"]["Enums"]["generation_frequency_enum"]
            | null
          name?: string | null
          support_priority?: boolean
        }
        Relationships: []
      }
      subscription_plans: {
        Row: {
          caps_id: string
          end_time: string | null
          id: string
          start_time: string | null
          stripe_price_monthly_id: string
          stripe_price_yearly_id: string
          stripe_product_id: string
        }
        Insert: {
          caps_id: string
          end_time?: string | null
          id?: string
          start_time?: string | null
          stripe_price_monthly_id: string
          stripe_price_yearly_id: string
          stripe_product_id: string
        }
        Update: {
          caps_id?: string
          end_time?: string | null
          id?: string
          start_time?: string | null
          stripe_price_monthly_id?: string
          stripe_price_yearly_id?: string
          stripe_product_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "subscription_plans_caps_id_fk"
            columns: ["caps_id"]
            isOneToOne: false
            referencedRelation: "subscription_caps"
            referencedColumns: ["id"]
          },
        ]
      }
      user_abflag: {
        Row: {
          flag: string
          user_id: string
          value: string | null
        }
        Insert: {
          flag: string
          user_id: string
          value?: string | null
        }
        Update: {
          flag?: string
          user_id?: string
          value?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "user_abflag_users_id_fk"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      user_api_keys: {
        Row: {
          created_at: string
          deleted_at: string | null
          enabled: boolean
          hmac: string
          id: string
          label: string | null
          user_id: string
        }
        Insert: {
          created_at?: string
          deleted_at?: string | null
          enabled?: boolean
          hmac: string
          id?: string
          label?: string | null
          user_id: string
        }
        Update: {
          created_at?: string
          deleted_at?: string | null
          enabled?: boolean
          hmac?: string
          id?: string
          label?: string | null
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "user_api_keys_user_id_users_id_fk"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      user_subscriptions: {
        Row: {
          interval: Database["public"]["Enums"]["subscription_interval"]
          plan_id: string
          rebill_time: string
          status: Database["public"]["Enums"]["subscription_status"]
          stripe_subscription_id: string
          user_id: string
        }
        Insert: {
          interval: Database["public"]["Enums"]["subscription_interval"]
          plan_id: string
          rebill_time: string
          status: Database["public"]["Enums"]["subscription_status"]
          stripe_subscription_id: string
          user_id: string
        }
        Update: {
          interval?: Database["public"]["Enums"]["subscription_interval"]
          plan_id?: string
          rebill_time?: string
          status?: Database["public"]["Enums"]["subscription_status"]
          stripe_subscription_id?: string
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "user_subscriptions_plan_id_fk"
            columns: ["plan_id"]
            isOneToOne: false
            referencedRelation: "subscription_plans"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "user_subscriptions_users_id_fk"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "users"
            referencedColumns: ["id"]
          },
        ]
      }
      users: {
        Row: {
          created_at: string
          dataroom_private: boolean | null
          default_view: Database["public"]["Enums"]["default_view_type"] | null
          email: string
          hyperReportSkipped: boolean
          id: string
          investor_deck_flow:
            | Database["public"]["Enums"]["investor_deck_flow"]
            | null
          isEmailConfirmed: boolean
          isHyperReportReady: boolean
          isInvestorDeckReady: boolean
          last_market_content_generated_at: string | null
          market_report_generation_status:
            | Database["public"]["Enums"]["market_content_generation_status"]
            | null
          name: string | null
          onboarding_completed_at: string | null
          role: string | null
          sharecode: string | null
          skip_market_content_generation: boolean
          stripe_customer_id: string | null
          uploadFinished: boolean
        }
        Insert: {
          created_at?: string
          dataroom_private?: boolean | null
          default_view?: Database["public"]["Enums"]["default_view_type"] | null
          email: string
          hyperReportSkipped?: boolean
          id?: string
          investor_deck_flow?:
            | Database["public"]["Enums"]["investor_deck_flow"]
            | null
          isEmailConfirmed?: boolean
          isHyperReportReady?: boolean
          isInvestorDeckReady?: boolean
          last_market_content_generated_at?: string | null
          market_report_generation_status?:
            | Database["public"]["Enums"]["market_content_generation_status"]
            | null
          name?: string | null
          onboarding_completed_at?: string | null
          role?: string | null
          sharecode?: string | null
          skip_market_content_generation?: boolean
          stripe_customer_id?: string | null
          uploadFinished?: boolean
        }
        Update: {
          created_at?: string
          dataroom_private?: boolean | null
          default_view?: Database["public"]["Enums"]["default_view_type"] | null
          email?: string
          hyperReportSkipped?: boolean
          id?: string
          investor_deck_flow?:
            | Database["public"]["Enums"]["investor_deck_flow"]
            | null
          isEmailConfirmed?: boolean
          isHyperReportReady?: boolean
          isInvestorDeckReady?: boolean
          last_market_content_generated_at?: string | null
          market_report_generation_status?:
            | Database["public"]["Enums"]["market_content_generation_status"]
            | null
          name?: string | null
          onboarding_completed_at?: string | null
          role?: string | null
          sharecode?: string | null
          skip_market_content_generation?: boolean
          stripe_customer_id?: string | null
          uploadFinished?: boolean
        }
        Relationships: []
      }
    }
    Views: {
      file_extractions_newest: {
        Row: {
          created_at: string | null
          error: Json | null
          extracted: Json | null
          file_id: string | null
          file_prompt_id: string | null
          file_prompt_version: number | null
        }
        Relationships: [
          {
            foreignKeyName: "file_extractions_file_prompts_id_version_fk"
            columns: ["file_prompt_id", "file_prompt_version"]
            isOneToOne: false
            referencedRelation: "file_prompts"
            referencedColumns: ["id", "version"]
          },
          {
            foreignKeyName: "file_extractions_file_prompts_id_version_fk"
            columns: ["file_prompt_id", "file_prompt_version"]
            isOneToOne: false
            referencedRelation: "file_prompts_newest"
            referencedColumns: ["id", "version"]
          },
          {
            foreignKeyName: "file_extractions_files_id_fk"
            columns: ["file_id"]
            isOneToOne: false
            referencedRelation: "files"
            referencedColumns: ["id"]
          },
        ]
      }
      file_prompts_newest: {
        Row: {
          active: boolean | null
          created_at: string | null
          exclusive: boolean | null
          id: string | null
          name: string | null
          prompt: string | null
          prompt_adv: Json | null
          purpose: Database["public"]["Enums"]["file_purpose"] | null
          schema: Json | null
          target_action_item:
            | Database["public"]["Enums"]["conversation_type"]
            | null
          tokens: number | null
          type: Database["public"]["Enums"]["file_type"] | null
          version: number | null
        }
        Relationships: []
      }
    }
    Functions: {
      bytea_to_base58: { Args: { input: string }; Returns: string }
      uuid_to_bytea: { Args: { input: string }; Returns: string }
    }
    Enums: {
      conversation_stage: "initial" | "edit"
      conversation_type:
        | "investor-deck"
        | "executive-summary"
        | "elevator-pitch"
        | "founders-letter"
        | "hyper-report"
        | "business-plan"
        | "one-pager"
        | "use-of-proceeds"
        | "financial-summary"
      default_view_type: "founder" | "investor"
      file_flavor: "pdf" | "doc" | "ppt"
      file_provider: "supabase" | "cloudflare"
      file_purpose: "onboarding" | "hyper-report"
      file_status: "uploaded" | "processed" | "error" | "reprocessing"
      file_type:
        | "pitchDeck"
        | "executiveSummary"
        | "offeringDocs"
        | "financialModels"
        | "businessPlan"
        | "other"
        | "investmentTerms"
        | "useOfProceeds"
        | "ipSummary"
        | "investorDeck"
      generated_document_type:
        | "investor-deck"
        | "business-plan"
        | "hyper-report"
        | "executive-summary"
        | "elevator-pitch"
        | "founders-letter"
        | "one-pager"
        | "use-of-proceeds"
        | "financial-summary"
      generation_frequency_enum: "monthly" | "weekly" | "daily"
      integration_type: "hubspot" | "mailchimp"
      investor_deck_flow: "legacy" | "document-first" | "conversation-first"
      market_content_generation_status: "in-progress" | "done" | "error"
      message_author: "agent" | "user"
      notification_type: "dataroom_visit"
      subscription_interval: "month" | "year"
      subscription_status:
        | "trialing"
        | "active"
        | "canceled"
        | "incomplete"
        | "incomplete_expired"
        | "past_due"
        | "unpaid"
        | "paused"
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  storage: {
    Tables: {
      buckets: {
        Row: {
          allowed_mime_types: string[] | null
          avif_autodetection: boolean | null
          created_at: string | null
          file_size_limit: number | null
          id: string
          name: string
          owner: string | null
          owner_id: string | null
          public: boolean | null
          type: Database["storage"]["Enums"]["buckettype"]
          updated_at: string | null
        }
        Insert: {
          allowed_mime_types?: string[] | null
          avif_autodetection?: boolean | null
          created_at?: string | null
          file_size_limit?: number | null
          id: string
          name: string
          owner?: string | null
          owner_id?: string | null
          public?: boolean | null
          type?: Database["storage"]["Enums"]["buckettype"]
          updated_at?: string | null
        }
        Update: {
          allowed_mime_types?: string[] | null
          avif_autodetection?: boolean | null
          created_at?: string | null
          file_size_limit?: number | null
          id?: string
          name?: string
          owner?: string | null
          owner_id?: string | null
          public?: boolean | null
          type?: Database["storage"]["Enums"]["buckettype"]
          updated_at?: string | null
        }
        Relationships: []
      }
      buckets_analytics: {
        Row: {
          created_at: string
          format: string
          id: string
          type: Database["storage"]["Enums"]["buckettype"]
          updated_at: string
        }
        Insert: {
          created_at?: string
          format?: string
          id: string
          type?: Database["storage"]["Enums"]["buckettype"]
          updated_at?: string
        }
        Update: {
          created_at?: string
          format?: string
          id?: string
          type?: Database["storage"]["Enums"]["buckettype"]
          updated_at?: string
        }
        Relationships: []
      }
      migrations: {
        Row: {
          executed_at: string | null
          hash: string
          id: number
          name: string
        }
        Insert: {
          executed_at?: string | null
          hash: string
          id: number
          name: string
        }
        Update: {
          executed_at?: string | null
          hash?: string
          id?: number
          name?: string
        }
        Relationships: []
      }
      objects: {
        Row: {
          bucket_id: string | null
          created_at: string | null
          id: string
          last_accessed_at: string | null
          level: number | null
          metadata: Json | null
          name: string | null
          owner: string | null
          owner_id: string | null
          path_tokens: string[] | null
          updated_at: string | null
          user_metadata: Json | null
          version: string | null
        }
        Insert: {
          bucket_id?: string | null
          created_at?: string | null
          id?: string
          last_accessed_at?: string | null
          level?: number | null
          metadata?: Json | null
          name?: string | null
          owner?: string | null
          owner_id?: string | null
          path_tokens?: string[] | null
          updated_at?: string | null
          user_metadata?: Json | null
          version?: string | null
        }
        Update: {
          bucket_id?: string | null
          created_at?: string | null
          id?: string
          last_accessed_at?: string | null
          level?: number | null
          metadata?: Json | null
          name?: string | null
          owner?: string | null
          owner_id?: string | null
          path_tokens?: string[] | null
          updated_at?: string | null
          user_metadata?: Json | null
          version?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "objects_bucketId_fkey"
            columns: ["bucket_id"]
            isOneToOne: false
            referencedRelation: "buckets"
            referencedColumns: ["id"]
          },
        ]
      }
      prefixes: {
        Row: {
          bucket_id: string
          created_at: string | null
          level: number
          name: string
          updated_at: string | null
        }
        Insert: {
          bucket_id: string
          created_at?: string | null
          level?: number
          name: string
          updated_at?: string | null
        }
        Update: {
          bucket_id?: string
          created_at?: string | null
          level?: number
          name?: string
          updated_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "prefixes_bucketId_fkey"
            columns: ["bucket_id"]
            isOneToOne: false
            referencedRelation: "buckets"
            referencedColumns: ["id"]
          },
        ]
      }
      s3_multipart_uploads: {
        Row: {
          bucket_id: string
          created_at: string
          id: string
          in_progress_size: number
          key: string
          owner_id: string | null
          upload_signature: string
          user_metadata: Json | null
          version: string
        }
        Insert: {
          bucket_id: string
          created_at?: string
          id: string
          in_progress_size?: number
          key: string
          owner_id?: string | null
          upload_signature: string
          user_metadata?: Json | null
          version: string
        }
        Update: {
          bucket_id?: string
          created_at?: string
          id?: string
          in_progress_size?: number
          key?: string
          owner_id?: string | null
          upload_signature?: string
          user_metadata?: Json | null
          version?: string
        }
        Relationships: [
          {
            foreignKeyName: "s3_multipart_uploads_bucket_id_fkey"
            columns: ["bucket_id"]
            isOneToOne: false
            referencedRelation: "buckets"
            referencedColumns: ["id"]
          },
        ]
      }
      s3_multipart_uploads_parts: {
        Row: {
          bucket_id: string
          created_at: string
          etag: string
          id: string
          key: string
          owner_id: string | null
          part_number: number
          size: number
          upload_id: string
          version: string
        }
        Insert: {
          bucket_id: string
          created_at?: string
          etag: string
          id?: string
          key: string
          owner_id?: string | null
          part_number: number
          size?: number
          upload_id: string
          version: string
        }
        Update: {
          bucket_id?: string
          created_at?: string
          etag?: string
          id?: string
          key?: string
          owner_id?: string | null
          part_number?: number
          size?: number
          upload_id?: string
          version?: string
        }
        Relationships: [
          {
            foreignKeyName: "s3_multipart_uploads_parts_bucket_id_fkey"
            columns: ["bucket_id"]
            isOneToOne: false
            referencedRelation: "buckets"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "s3_multipart_uploads_parts_upload_id_fkey"
            columns: ["upload_id"]
            isOneToOne: false
            referencedRelation: "s3_multipart_uploads"
            referencedColumns: ["id"]
          },
        ]
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      add_prefixes: {
        Args: { _bucket_id: string; _name: string }
        Returns: undefined
      }
      can_insert_object: {
        Args: { bucketid: string; metadata: Json; name: string; owner: string }
        Returns: undefined
      }
      delete_leaf_prefixes: {
        Args: { bucket_ids: string[]; names: string[] }
        Returns: undefined
      }
      delete_prefix: {
        Args: { _bucket_id: string; _name: string }
        Returns: boolean
      }
      extension: { Args: { name: string }; Returns: string }
      filename: { Args: { name: string }; Returns: string }
      foldername: { Args: { name: string }; Returns: string[] }
      get_level: { Args: { name: string }; Returns: number }
      get_prefix: { Args: { name: string }; Returns: string }
      get_prefixes: { Args: { name: string }; Returns: string[] }
      get_size_by_bucket: {
        Args: never
        Returns: {
          bucket_id: string
          size: number
        }[]
      }
      list_multipart_uploads_with_delimiter: {
        Args: {
          bucket_id: string
          delimiter_param: string
          max_keys?: number
          next_key_token?: string
          next_upload_token?: string
          prefix_param: string
        }
        Returns: {
          created_at: string
          id: string
          key: string
        }[]
      }
      list_objects_with_delimiter: {
        Args: {
          bucket_id: string
          delimiter_param: string
          max_keys?: number
          next_token?: string
          prefix_param: string
          start_after?: string
        }
        Returns: {
          id: string
          metadata: Json
          name: string
          updated_at: string
        }[]
      }
      lock_top_prefixes: {
        Args: { bucket_ids: string[]; names: string[] }
        Returns: undefined
      }
      operation: { Args: never; Returns: string }
      search: {
        Args: {
          bucketname: string
          levels?: number
          limits?: number
          offsets?: number
          prefix: string
          search?: string
          sortcolumn?: string
          sortorder?: string
        }
        Returns: {
          created_at: string
          id: string
          last_accessed_at: string
          metadata: Json
          name: string
          updated_at: string
        }[]
      }
      search_legacy_v1: {
        Args: {
          bucketname: string
          levels?: number
          limits?: number
          offsets?: number
          prefix: string
          search?: string
          sortcolumn?: string
          sortorder?: string
        }
        Returns: {
          created_at: string
          id: string
          last_accessed_at: string
          metadata: Json
          name: string
          updated_at: string
        }[]
      }
      search_v1_optimised: {
        Args: {
          bucketname: string
          levels?: number
          limits?: number
          offsets?: number
          prefix: string
          search?: string
          sortcolumn?: string
          sortorder?: string
        }
        Returns: {
          created_at: string
          id: string
          last_accessed_at: string
          metadata: Json
          name: string
          updated_at: string
        }[]
      }
      search_v2: {
        Args: {
          bucket_name: string
          levels?: number
          limits?: number
          prefix: string
          sort_column?: string
          sort_column_after?: string
          sort_order?: string
          start_after?: string
        }
        Returns: {
          created_at: string
          id: string
          key: string
          last_accessed_at: string
          metadata: Json
          name: string
          updated_at: string
        }[]
      }
    }
    Enums: {
      buckettype: "STANDARD" | "ANALYTICS"
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

type DatabaseWithoutInternals = Omit<Database, "__InternalSupabase">

type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, "public">]

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends {
      Row: infer R
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] &
        DefaultSchema["Views"])
    ? (DefaultSchema["Tables"] &
        DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R
      }
      ? R
      : never
    : never

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Insert: infer I
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I
      }
      ? I
      : never
    : never

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Update: infer U
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U
      }
      ? U
      : never
    : never

export type Enums<
  DefaultSchemaEnumNameOrOptions extends
    | keyof DefaultSchema["Enums"]
    | { schema: keyof DatabaseWithoutInternals },
  EnumName extends DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
    : never = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"]
    ? DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
    : never

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema["CompositeTypes"]
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends PublicCompositeTypeNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"]
    : never = never,
> = PublicCompositeTypeNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"]
    ? DefaultSchema["CompositeTypes"][PublicCompositeTypeNameOrOptions]
    : never

export const Constants = {
  internal: {
    Enums: {},
  },
  public: {
    Enums: {
      conversation_stage: ["initial", "edit"],
      conversation_type: [
        "investor-deck",
        "executive-summary",
        "elevator-pitch",
        "founders-letter",
        "hyper-report",
        "business-plan",
        "one-pager",
        "use-of-proceeds",
        "financial-summary",
      ],
      default_view_type: ["founder", "investor"],
      file_flavor: ["pdf", "doc", "ppt"],
      file_provider: ["supabase", "cloudflare"],
      file_purpose: ["onboarding", "hyper-report"],
      file_status: ["uploaded", "processed", "error", "reprocessing"],
      file_type: [
        "pitchDeck",
        "executiveSummary",
        "offeringDocs",
        "financialModels",
        "businessPlan",
        "other",
        "investmentTerms",
        "useOfProceeds",
        "ipSummary",
        "investorDeck",
      ],
      generated_document_type: [
        "investor-deck",
        "business-plan",
        "hyper-report",
        "executive-summary",
        "elevator-pitch",
        "founders-letter",
        "one-pager",
        "use-of-proceeds",
        "financial-summary",
      ],
      generation_frequency_enum: ["monthly", "weekly", "daily"],
      integration_type: ["hubspot", "mailchimp"],
      investor_deck_flow: ["legacy", "document-first", "conversation-first"],
      market_content_generation_status: ["in-progress", "done", "error"],
      message_author: ["agent", "user"],
      notification_type: ["dataroom_visit"],
      subscription_interval: ["month", "year"],
      subscription_status: [
        "trialing",
        "active",
        "canceled",
        "incomplete",
        "incomplete_expired",
        "past_due",
        "unpaid",
        "paused",
      ],
    },
  },
  storage: {
    Enums: {
      buckettype: ["STANDARD", "ANALYTICS"],
    },
  },
} as const
